import { useCallback, useEffect, useState } from "react";
import { adminConfig, adminModels, adminSettings, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeSlash, Key, Crown, Lightning } from "@phosphor-icons/react";
import AdminCard from "../components/AdminCard";
import AdminBadge from "../components/AdminBadge";
import AdminFormSelect from "../components/AdminFormSelect";
import AdminPrimaryButton from "../components/AdminPrimaryButton";

function TierBadge({ tier }) {
  return (
    <AdminBadge variant={tier === "premium" ? "premium" : "success"}>
      {tier === "premium" ? <Crown size={10} weight="fill" /> : <Lightning size={10} weight="fill" />}
      {tier}
    </AdminBadge>
  );
}

export default function ConfigTab({ onSaved }) {
  const [data, setData] = useState(null);
  const [models, setModels] = useState(null);
  const [textModel, setTextModel] = useState("");
  const [imageModel, setImageModel] = useState("");
  const [aiDataModel, setAiDataModel] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, mlist, s] = await Promise.all([adminConfig(), adminModels(), adminSettings.get()]);
      setData(cfg);
      setModels(mlist);
      setTextModel(s.text_model || "");
      setImageModel(s.image_model || "");
      setAiDataModel(s.ai_data_model || "");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      await adminSettings.update({ text_model: textModel, image_model: imageModel, ai_data_model: aiDataModel });
      toast.success("Model selection saved");
      await load();
      await onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading || !data || !models) return <p className="text-slate-400 text-sm">Loading…</p>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="config-tab">
      {/* Security notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-700" data-testid="config-warning">
        <strong className="font-semibold">Security:</strong> API keys are masked and never exposed to non-admin users.
        The active model selection determines which key is used for generation. To use your own API keys, add a
        custom model in the <strong>Custom Models</strong> tab, then select it below.
      </div>

      {/* Model selection */}
      <AdminCard data-testid="model-selection">
        <div className="p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Active models</p>
          <h3 className="text-lg font-semibold text-slate-900 mt-1 mb-5">Choose the models used for generation</h3>
          <div className="grid md:grid-cols-2 gap-5">
            <AdminFormSelect
              label="Text generation model"
              value={textModel}
              onChange={(e) => setTextModel(e.target.value)}
              hint="Used for scripts, image prompts, video prompts."
              data-testid="text-model-select"
            >
              {models.text.map((m) => (
                <option key={m.id} value={m.id}>
                  [{m.tier.toUpperCase()}] {m.label} — {m.provider}{m.custom ? " · CUSTOM KEY" : ""}
                </option>
              ))}
            </AdminFormSelect>

            <AdminFormSelect
              label="Image generation model"
              value={imageModel}
              onChange={(e) => setImageModel(e.target.value)}
              hint='Used by Nano Banana when creators click "Generate AI Image".'
              data-testid="image-model-select"
            >
              {models.image.map((m) => (
                <option key={m.id} value={m.id}>
                  [{m.tier.toUpperCase()}] {m.label} — {m.provider}{m.custom ? " · CUSTOM KEY" : ""}
                </option>
              ))}
            </AdminFormSelect>

            <div className="md:col-span-2">
              <AdminFormSelect
                label="AI data jobs model"
                value={aiDataModel}
                onChange={(e) => setAiDataModel(e.target.value)}
                hint="Used by all 14 AI data fetch jobs. Must be a custom model with your own API key."
                data-testid="ai-data-model-select"
              >
                <option value="">— Use Emergent fallback (default) —</option>
                {models.text.filter((m) => m.custom).map((m) => (
                  <option key={m.id} value={m.id}>
                    [{m.tier.toUpperCase()}] {m.label} — {m.provider} · CUSTOM KEY
                  </option>
                ))}
              </AdminFormSelect>
            </div>
          </div>
          <div className="mt-6">
            <AdminPrimaryButton
              busy={busy}
              onClick={save}
              icon={<Key size={14} weight="bold" />}
              data-testid="save-models-button"
            >
              Apply selection
            </AdminPrimaryButton>
          </div>
        </div>
      </AdminCard>

      {/* Model catalog */}
      <AdminCard header="Available models · free vs premium" data-testid="model-catalog">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                {["Kind", "Model", "Provider", "Tier", "Active"].map((h) => (
                  <th key={h} className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {["text", "image"].flatMap((kind) =>
                models[kind].map((m) => {
                  const isText  = kind === "text"  && textModel === m.id;
                  const isImage = kind === "image" && imageModel === m.id;
                  const isAI    = kind === "text"  && aiDataModel === m.id;
                  const labels  = [isText && "text gen", isImage && "image gen", isAI && "ai data"].filter(Boolean);
                  return (
                    <tr key={kind + m.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`model-row-${m.id}`}>
                      <td className="px-6 py-3 text-xs font-medium uppercase tracking-wider text-slate-500">{kind}</td>
                      <td className="px-6 py-3 font-semibold text-slate-900">{m.label}</td>
                      <td className="px-6 py-3 text-slate-500 text-xs">{m.provider}</td>
                      <td className="px-6 py-3"><TierBadge tier={m.tier} /></td>
                      <td className="px-6 py-3 flex flex-wrap gap-1">
                        {labels.map((l) => (
                          <AdminBadge key={l} variant="admin">{l}</AdminBadge>
                        ))}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </AdminCard>

      {/* API Keys */}
      <div className="space-y-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Providers &amp; keys</p>
        {data.providers.map((p, i) => (
          <AdminCard key={i} data-testid={`provider-${i}`}>
            <div className="p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-semibold text-slate-900 text-lg">{p.name}</span>
                    <TierBadge tier={p.tier || "free"} />
                    <AdminBadge variant={p.status === "active" ? "success" : "error"}>{p.status}</AdminBadge>
                  </div>
                  <p className="text-sm text-slate-500">{p.purpose}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5 pt-5 border-t border-slate-100">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Model</div>
                  <div className="text-sm text-slate-700 break-all">{p.model_label || p.model}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Env var</div>
                  <div className={`text-sm break-all font-mono ${p.is_custom ? "text-indigo-600" : "text-slate-700"}`}>{p.key_env}</div>
                  {p.key_source && <div className="text-xs text-slate-400 mt-0.5">{p.key_source}</div>}
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Key</div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-700 font-mono">
                      {revealed[i] ? p.key_masked : "••••••••••••"}
                    </span>
                    <button
                      onClick={() => setRevealed((r) => ({ ...r, [i]: !r[i] }))}
                      className="text-slate-400 hover:text-slate-700 transition-colors"
                      data-testid={`toggle-reveal-${i}`}
                      aria-label="Reveal key"
                    >
                      {revealed[i] ? <EyeSlash size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </AdminCard>
        ))}
      </div>
    </div>
  );
}
